'use client';

import isPropValid from '@emotion/is-prop-valid';
import type { ButtonHTMLAttributes, CSSProperties, ReactNode, Ref } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';

import type { Spacing, SpacingType } from '@zoomit/primitives';
import { spacingHelper } from '@zoomit/primitives';

import type { ButtonSize} from '../../css/link-button';
import { linkButton } from '../../css/link-button';
import { theme } from '../../theme';
import type { ColorScheme } from '../../theme/button';
import { TextIcon } from '../text-icon';

export type ButtonVariant = 'outline' | 'contained' | 'text';

type BaseProps = {
  colorScheme?: ColorScheme;
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
  fontWeight?: CSSProperties['fontWeight'];
  spacing?: SpacingType;
  isLoading?: boolean;
  gap?: keyof typeof Spacing;
};

const Base = styled.button.withConfig({
  shouldForwardProp: (propName, target) => {
    if (typeof target === 'string') {
      return isPropValid(propName);
    }
    return true;
  },
})<BaseProps>`
  ${({ disabled, size = 'auto', variant = 'contained', colorScheme = 'default', fontWeight }) =>
    linkButton({ size, variant, colorScheme, disabled, fontWeight })};
  ${({ spacing }) => spacing && spacingHelper(spacing)};
`;

const LoadingSpinner = styled.div`
  margin: 0 auto;
  width: 16px;
  height: 16px;
  border-radius: var(${theme.radius.full});
  border: 2px solid;
  animation: spin 0.7s linear infinite;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(359deg);
    }
  }
`;

export type Props = ButtonHTMLAttributes<HTMLButtonElement> & BaseProps;

export const Button = forwardRef((props: Props, ref: Ref<HTMLButtonElement>) => {
  const {
    size = 'medium',
    variant = 'contained',
    leftIcon,
    rightIcon,
    colorScheme = 'default',
    children,
    gap = size === 'small' ? 'x' : 'x2',
    isLoading,
    disabled,
    ...rest
  } = props;

  return (
    <Base
      ref={ref}
      size={size}
      colorScheme={colorScheme}
      variant={variant}
      type="button"
      disabled={isLoading || disabled}
      {...rest}
    >
      {isLoading ? (
        <LoadingSpinner className="spinner" />
      ) : (
        <TextIcon gap={gap} leftIcon={leftIcon} rightIcon={rightIcon}>
          {children}
        </TextIcon>
      )}
    </Base>
  );
});
